import { createContext, createResource, createSignal, useContext, onMount, onCleanup, ParentProps, Setter } from "solid-js"

import SwarmApiV1, { GetPeersByEoaResponse, Gossip, Leaderboard, LoginResponse, NodesConnected, RoundStage, TopRewards, UniqueVoters } from "./swarm.apiv1"

// SwarmContextType is the interface of the SwarmContext.
export interface SwarmContextType {
	// Gossip info
	gossipMessages: () => Gossip | null | undefined
	gossipMessagesLoading: () => boolean
	gossipMessagesError: () => Error | null

	// The data for the actual leaderboard + loading, error states.
	leaders: () => Leaderboard | null | undefined
	leadersLoading: () => boolean
	leadersError: () => Error | null

	// The number of nodes connected to the swarm.
	nodesConnected: () => { count: number }
	nodesConnectedLoading: () => boolean
	nodesConnectedError: () => Error | null

	roundAndStage: () => RoundStage | null | undefined
	roundAndStageLoading: () => boolean
	roundAndStageError: () => Error | null

	// The number of unique voters *(all time) + loading, error states.
	uniqueVoters: () => { count: number }
	uniqueVotersLoading: () => boolean
	uniqueVotersError: () => Error | null

	// Rewards history info + loading, error states.
	rewardsHistory: () => TopRewards | null | undefined
	rewardsHistoryLoading: () => boolean
	rewardsHistoryError: () => Error | null

	currentUserPeers: () => GetPeersByEoaResponse | null | undefined
	currentUserPeersLoading: () => boolean
	currentUserPeersError: () => Error | null

	userEoa: () => `0x${string}` | null
	setUserEoa: Setter<`0x${string}` | null>
}

export const SwarmContext = createContext<SwarmContextType>()

export function useSwarm() {
	const context = useContext(SwarmContext)
	if (!context) {
		throw new Error("useSwarm must be used within a SwarmProvider")
	}
	return context
}

export function SwarmProvider(props: ParentProps) {
	const [userEoa, setUserEoa] = createSignal<`0x${string}` | null>(null)

	// Gossip state
	const [gossipMessages, setGossipMessages] = createSignal<Gossip | undefined | null>(null)
	let seenMessageIds = new Set<string>()

	// Round and stage state
	const [roundAndStage, setRoundAndStage] = createSignal<RoundStage | undefined | null>(null)

	// Leaderboard state
	const [leaders, setLeaders] = createSignal<Leaderboard | null | undefined>(null)

	// Top rewards state
	const [rewardsHistory, setRewardsHistory] = createSignal<TopRewards | null | undefined>(null)

	// Network stats
	const [nodesConnected, setNodesConnected] = createSignal<NodesConnected>({ count: 0 })
	const [uniqueVoters, setUniqueVoters] = createSignal<UniqueVoters>({ count: 0 })

	// ------------------------------------------------------------------------------------------------
	// Resources for data fetching + data state
	//
	// TODO: There's some cleanup to be done here.
	// We should be able to reuse the data from the resource rather than have signals for each one.
	// e.g., _leaderboardData could just be leaderboard and no need for a separate signal.
	// ------------------------------------------------------------------------------------------------

	// @ts-expect-warning - Intentionally unused variable
	const [_leaderboardData, { refetch: refetchLeaderboard }] = createResource(async () => {
		const data = await fetchLeaderboardData()
		if (!data || data.entries.length === 0) {
			setLeaders(null)
			return
		}

		setLeaders(data)

		return data
	})

	// @ts-expect-warning - Intentionally unused variable
	const [_roundAndStage, { refetch: refetchRoundAndStage }] = createResource(async () => {
		const data = await fetchRoundStage()
		if (!data) {
			return undefined
		}

		setRoundAndStage(data)

		return data
	})

	const [_nodesConnected, { refetch: refetchNodesConnected }] = createResource(async () => {
		const data = await fetchNodesConnected()
		if (!data) {
			return undefined
		}

		setNodesConnected(data)
		return data
	})

	const [_uniqueVoters, { refetch: refetchUniqueVoters }] = createResource(async () => {
		const data = await fetchUniqueVoters()
		if (!data) {
			return undefined
		}

		setUniqueVoters(data)
		return data
	})

	// @ts-expect-warning - Intentionally unused variable
	const [_gossipData, { refetch: refetchGossip }] = createResource(async () => {
		const data = await fetchGossipData()
		if (!data) {
			return undefined
		}

		// Filter out messages that have already been seen.
		const msgs = data.messages
			.filter((msg) => !seenMessageIds.has(msg.id))
			.map((msg) => {
				seenMessageIds.add(msg.id)
				return msg
			})

		// Concatenate the old messages with the new ones, and limit to 200.
		// 200 is arbitrarily chosen.
		const nextGossip = {
			messages: [...(gossipMessages()?.messages ?? []), ...msgs].slice(-200),
		}

		setGossipMessages(nextGossip)

		// Truncate the set of seen message IDs to 2000 to prevent it from growing indefinitely.
		// 2000 is also arbitrarily chosen.
		if (seenMessageIds.size > 2000) {
			const temp = Array.from(seenMessageIds).slice(-2000)
			seenMessageIds = new Set(temp)
		}

		return nextGossip
	})

	// @ts-expect-warning - Intentionally unused variable
	const [_rewardsHistory, { refetch: refetchRewardsHistory }] = createResource(async () => {
		const data = await fetchRewardsHistory()
		setRewardsHistory(data)
		return data
	})

	// currentUserPeers will only be fetched if userEoa is set, and will automatically re-fetch when userEoa changes.
	const [currentUserPeers, { refetch: refetchCurrentUserPeers }] = createResource(userEoa, async () => {
		const data = await fetchCurrentUserPeers()
		return data
	})

	// ------------------------------------------------------------------------------------------------
	// Polling
	// ------------------------------------------------------------------------------------------------

	// Polling timers
	let leaderboardTimer: ReturnType<typeof setTimeout> | undefined = undefined
	let roundAndStageTimer: ReturnType<typeof setTimeout> | undefined = undefined
	let nodesConnectedTimer: ReturnType<typeof setTimeout> | undefined = undefined
	let uniqueVotersTimer: ReturnType<typeof setTimeout> | undefined = undefined
	let gossipTimer: ReturnType<typeof setTimeout> | undefined = undefined
	let rewardsHistoryTimer: ReturnType<typeof setTimeout> | undefined = undefined
	let currentUserPeersTimer: ReturnType<typeof setTimeout> | undefined = undefined

	// Polling functions
	const pollLeaderboard = async () => {
		await refetchLeaderboard()

		if (leaderboardTimer !== undefined) {
			clearTimeout(leaderboardTimer)
		}

		leaderboardTimer = setTimeout(pollLeaderboard, 5 * 60_000)
	}

	const pollRoundAndStage = async () => {
		await refetchRoundAndStage()

		if (roundAndStageTimer !== undefined) {
			clearTimeout(roundAndStageTimer)
		}

		roundAndStageTimer = setTimeout(pollRoundAndStage, 5 * 60_000)
	}

	const pollNodesConnected = async () => {
		await refetchNodesConnected()

		if (nodesConnectedTimer !== undefined) {
			clearTimeout(nodesConnectedTimer)
		}

		nodesConnectedTimer = setTimeout(pollNodesConnected, 5 * 60_000)
	}

	const pollUniqueVoters = async () => {
		await refetchUniqueVoters()

		if (uniqueVotersTimer !== undefined) {
			clearTimeout(uniqueVotersTimer)
		}

		uniqueVotersTimer = setTimeout(pollUniqueVoters, 5 * 60_000)
	}

	const pollGossip = async () => {
		await refetchGossip()

		if (gossipTimer !== undefined) {
			clearTimeout(gossipTimer)
		}

		gossipTimer = setTimeout(pollGossip, 5 * 60_000)
	}

	const pollRewardsHistory = async () => {
		await refetchRewardsHistory()

		if (rewardsHistoryTimer !== undefined) {
			clearTimeout(rewardsHistoryTimer)
		}

		rewardsHistoryTimer = setTimeout(pollRewardsHistory, 10_000)
	}

	const pollCurrentUserPeers = async () => {
		await refetchCurrentUserPeers()

		if (currentUserPeersTimer !== undefined) {
			clearTimeout(currentUserPeersTimer)
		}

		currentUserPeersTimer = setTimeout(pollCurrentUserPeers, 5 * 60_000)
	}

	// Setup and cleanup
	onMount(() => {
		// These already fire once immediately since the calls are created through createResource,
		// so we can add a 10s delay to start firing after that.
		leaderboardTimer = setTimeout(pollLeaderboard, 5 * 60_000)
		roundAndStageTimer = setTimeout(pollRoundAndStage, 5 * 60_000)
		nodesConnectedTimer = setTimeout(pollNodesConnected, 5 * 60_000)
		gossipTimer = setTimeout(pollGossip, 5 * 60_000)
		uniqueVotersTimer = setTimeout(pollUniqueVoters, 5 * 60_000)
		rewardsHistoryTimer = setTimeout(pollRewardsHistory, 10_000)
		currentUserPeersTimer = setTimeout(pollCurrentUserPeers, 5 * 60_000)
	})

	onCleanup(() => {
		if (leaderboardTimer) {
			clearTimeout(leaderboardTimer)
		}

		if (roundAndStageTimer) {
			clearTimeout(roundAndStageTimer)
		}

		if (nodesConnectedTimer) {
			clearTimeout(nodesConnectedTimer)
		}

		if (uniqueVotersTimer) {
			clearTimeout(uniqueVotersTimer)
		}

		if (rewardsHistoryTimer) {
			clearTimeout(rewardsHistoryTimer)
		}

		if (gossipTimer) {
			clearTimeout(gossipTimer)
		}

		if (currentUserPeersTimer) {
			clearTimeout(currentUserPeersTimer)
		}
	})

	const value: SwarmContextType = {
		gossipMessages,
		gossipMessagesLoading: () => _gossipData.loading,
		gossipMessagesError: () => _gossipData.error,

		leaders,
		leadersLoading: () => _leaderboardData.loading,
		leadersError: () => _leaderboardData.error,

		nodesConnected,
		nodesConnectedLoading: () => _nodesConnected.loading,
		nodesConnectedError: () => _nodesConnected.error,

		roundAndStage,
		roundAndStageLoading: () => _roundAndStage.loading,
		roundAndStageError: () => _roundAndStage.error,

		uniqueVoters,
		uniqueVotersLoading: () => _uniqueVoters.loading,
		uniqueVotersError: () => _uniqueVoters.error,

		rewardsHistory,
		rewardsHistoryLoading: () => _rewardsHistory.loading,
		rewardsHistoryError: () => _rewardsHistory.error,

		currentUserPeers,
		currentUserPeersLoading: () => currentUserPeers.loading,
		currentUserPeersError: () => currentUserPeers.error,

		userEoa,
		setUserEoa,
	}

	return <SwarmContext.Provider value={value}>{props.children}</SwarmContext.Provider>
}

// ------------------------------------------------------------------------------------------------
// Wrappers around the API
// ------------------------------------------------------------------------------------------------

async function fetchLeaderboardData(): Promise<Leaderboard | undefined> {
	try {
		return await SwarmApiV1.getLeaderboard()
	} catch (e) {
		console.error("fetchLeaderboardData failed", e)
		return undefined
	}
}

async function fetchRoundStage(): Promise<RoundStage | undefined> {
	try {
		return await SwarmApiV1.getRoundStage()
	} catch (e) {
		console.error("fetchRoundStage failed", e)
		return undefined
	}
}

async function fetchNodesConnected(): Promise<NodesConnected | undefined> {
	try {
		return await SwarmApiV1.getNodesConnected()
	} catch (e) {
		console.error("fetchNodesConnected failed", e)
		return undefined
	}
}

async function fetchUniqueVoters(): Promise<UniqueVoters | undefined> {
	try {
		return await SwarmApiV1.getUniqueVoters()
	} catch (e) {
		console.error("fetchUniqueVoters failed", e)
		return undefined
	}
}

async function fetchGossipData(): Promise<Gossip | undefined> {
	try {
		return await SwarmApiV1.getGossipMessages()
	} catch (e) {
		console.error("fetchGossipData failed", e)
		return undefined
	}
}

async function fetchRewardsHistory(): Promise<TopRewards | undefined> {
	try {
		return await SwarmApiV1.getTopRewards()
	} catch (e) {
		console.error("fetchRewardsHistoryData failed", e)
		return undefined
	}
}

async function fetchCurrentUserPeers(): Promise<GetPeersByEoaResponse | undefined> {
	try {
		return await SwarmApiV1.getPeersForUser()
	} catch (e) {
		console.error("fetchCurrentUserPeers failed", e)
		return undefined
	}
}
